import { createApiClient, type FetchLike } from '#shared/core/api-client';
import type { AnyRoute } from '#shared/core/contract';
import { ApiError, isApiError } from '#shared/core/errors';
import { APP_VERSION_HEADER, CLIENT_HEADER, DISPLAY_HEADER } from '#shared/schemas/device';
import { displayHeader } from './displayMode';
import { useSignals } from './signals';

/**
 * The one typed API client. Every call sends X-TTS-Display (standalone or browser) and
 * X-TTS-Client, so the server's install gate and CSRF check see them. Responses are checked:
 * - 428 INSTALL_REQUIRED: the shell renders the install gate (render decision, no redirect).
 * - 401 on a member route: the shell drops to Welcome.
 * - 426 CLIENT_OUTDATED: the update prompt turns blocking (the outbox keeps its rows, see outbox.ts).
 * - X-App-Version is remembered for the update prompt.
 *
 * Use it as `api.call(contract, { params, query, body })`. For SSE and file routes use
 * `api.url(contract, { params })`.
 */

/** Sent as X-TTS-Client (arch.md section 2 spells the value as `1`). */
export const CLIENT_ID = '1';

/** This build's version: `<package>+<build stamp>`, or '0.0.0-dev' where the build did not define it (tests). */
export const BUILD_VERSION: string = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : '0.0.0-dev';

/**
 * X-App-Version is what lets the server answer 426 CLIENT_OUTDATED to a build below its
 * `minClient`. The server compares the numbers and ignores the +build stamp.
 */
export function ttsHeaders(): Record<string, string> {
  return { [DISPLAY_HEADER]: displayHeader(), [CLIENT_HEADER]: CLIENT_ID, [APP_VERSION_HEADER]: BUILD_VERSION };
}

const browserFetch: FetchLike = (url, init) =>
  globalThis.fetch(url, {
    method: init.method,
    headers: init.headers,
    body: init.body as BodyInit | undefined,
    credentials: init.credentials,
    signal: init.signal as AbortSignal | undefined,
  });

function onResponse(res: { status: number; headers: { get(n: string): string | null } }, route: AnyRoute): void {
  const signals = useSignals.getState();
  const v = res.headers.get(APP_VERSION_HEADER);
  if (v) signals.sawServerVersion(v);
  if (res.status === 428) signals.forceGate();
  else if (res.status === 401 && route.auth !== 'pub') signals.signedOut();
  else if (res.status === 426) signals.requireUpdate();
}

export const api = createApiClient({
  fetch: browserFetch,
  headers: ttsHeaders,
  onResponse,
  credentials: 'same-origin',
});

/**
 * POST JSON to a URL that is not a typed call (the outbox posts to per-stream URLs).
 * Same headers and the same 428/401 handling as `api.call`.
 */
export async function postJson<T>(url: string, body: unknown, signal?: AbortSignal): Promise<T> {
  const res = await globalThis.fetch(url, {
    method: 'POST',
    headers: { accept: 'application/json', 'content-type': 'application/json', ...ttsHeaders() },
    body: JSON.stringify(body),
    credentials: 'same-origin',
    signal,
  });
  onResponse(res, { auth: 'mem' } as AnyRoute);
  const text = await res.text();
  const json: unknown = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    const e = (json as { error?: { code?: string; message?: string } } | undefined)?.error;
    throw new ApiError(e?.code ?? 'INTERNAL', e?.message ?? `HTTP ${res.status}`, { status: res.status });
  }
  return json as T;
}

/** True for errors worth retrying later (no signal, server hiccup, rate limit). */
export function isRetryable(e: unknown): boolean {
  if (!isApiError(e)) return true; // TypeError from fetch: offline or DNS
  return e.status >= 500 || e.status === 408 || e.status === 429;
}

export function isUnauthenticated(e: unknown): boolean {
  return isApiError(e) && e.status === 401;
}

export { ApiError, isApiError };
